import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { 
    Trophy, 
    Crown,
    Medal,
    Star,
    Flame,
    TrendingUp,
    Users,
    Sparkles,
    ChevronRight,
    Zap
} from 'lucide-react';
import { formatAddress } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Podium component for top 3
function Podium({ players }) {
    if (!players || players.length < 3) return null;
    
    const podiumOrder = [players[1], players[0], players[2]]; // 2nd, 1st, 3rd
    const heights = ['h-24', 'h-32', 'h-20'];
    const positions = ['2nd', '1st', '3rd'];
    const colors = ['#C0C0C0', '#FFD700', '#CD7F32'];
    const icons = [Medal, Crown, Medal];
    
    return (
        <div className="flex items-end justify-center gap-4 mb-8">
            {podiumOrder.map((player, idx) => {
                if (!player) return null;
                const Icon = icons[idx];
                const isFirst = idx === 1;
                
                return (
                    <div key={idx} className="flex flex-col items-center">
                        {/* Player Info */}
                        <Link to={`/profile/${player.address}`} className="text-center mb-2 group">
                            <div 
                                className={`w-16 h-16 ${isFirst ? 'w-20 h-20' : ''} rounded-full flex items-center justify-center mb-2 mx-auto border-2 transition-transform group-hover:scale-110`}
                                style={{ 
                                    backgroundColor: `${player.level?.color || '#FFF'}20`,
                                    borderColor: player.level?.color || '#FFF'
                                }}
                            >
                                <span className="text-2xl font-bold" style={{ color: player.level?.color }}>
                                    {player.level?.level || 1}
                                </span>
                            </div>
                            <div className="text-white font-semibold text-sm group-hover:text-[#00FF66] transition-colors">
                                {formatAddress(player.address)}
                            </div>
                            <div className="text-[#00FF66] font-mono text-sm">
                                {player.xp?.toLocaleString()} XP
                            </div>
                            <div className="flex items-center justify-center gap-1 mt-1">
                                <Trophy className="w-3 h-3 text-[#FFD700]" />
                                <span className="text-xs text-white/50">{player.badge_count} badges</span>
                            </div>
                        </Link>
                        
                        {/* Podium Block */}
                        <div 
                            className={`${heights[idx]} w-24 ${isFirst ? 'w-28' : ''} rounded-t-lg flex items-center justify-center`}
                            style={{ backgroundColor: `${colors[idx]}30`, borderTop: `3px solid ${colors[idx]}` }}
                        >
                            <Icon className="w-8 h-8" style={{ color: colors[idx] }} />
                        </div>
                        <div 
                            className="text-center py-2 w-24 rounded-b-lg font-bold"
                            style={{ backgroundColor: `${colors[idx]}20`, color: colors[idx] }}
                        >
                            {positions[idx]}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// Leaderboard Row
function LeaderboardRow({ player, rank }) {
    const isTopThree = rank <= 3;
    const rankColors = {
        1: '#FFD700',
        2: '#C0C0C0',
        3: '#CD7F32'
    };
    
    return (
        <Link 
            to={`/profile/${player.address}`}
            className="flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-[#00FF66]/30 hover:bg-white/[0.04] transition-all group"
        >
            {/* Rank */}
            <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${isTopThree ? '' : 'bg-white/5'}`}
                style={isTopThree ? { backgroundColor: `${rankColors[rank]}20`, color: rankColors[rank] } : { color: 'rgba(255,255,255,0.5)' }}
            >
                {isTopThree ? (
                    rank === 1 ? <Crown className="w-5 h-5" /> : <Medal className="w-5 h-5" />
                ) : (
                    <span>#{rank}</span>
                )}
            </div>
            
            {/* Level Badge */}
            <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg"
                style={{ 
                    backgroundColor: `${player.level?.color || '#FFF'}15`,
                    color: player.level?.color || '#FFF',
                    border: `1px solid ${player.level?.color || '#FFF'}30`
                }}
            >
                {player.level?.level || 1}
            </div>
            
            {/* Player Info */}
            <div className="flex-1">
                <div className="flex items-center gap-2">
                    <span className="text-white font-semibold group-hover:text-[#00FF66] transition-colors">
                        {formatAddress(player.address)}
                    </span>
                    <Badge 
                        className="text-xs"
                        style={{ 
                            backgroundColor: `${player.level?.color || '#FFF'}20`,
                            color: player.level?.color || '#FFF'
                        }}
                    >
                        {player.level?.name || 'Novice'}
                    </Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-white/50 mt-1">
                    <span className="flex items-center gap-1">
                        <Trophy className="w-3 h-3 text-[#FFD700]" />
                        {player.badge_count || 0} badges
                    </span>
                    <span className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3 text-[#00FF66]" />
                        {player.stats?.wins || 0} wins
                    </span>
                    <span className="flex items-center gap-1">
                        <Flame className="w-3 h-3 text-[#FF6B6B]" />
                        {player.stats?.max_streak || 0} streak
                    </span>
                </div>
            </div>
            
            {/* XP */}
            <div className="text-right">
                <div className="font-mono text-xl font-bold text-[#00FF66]">
                    {player.xp?.toLocaleString() || 0}
                </div>
                <div className="text-xs text-white/40">XP</div>
            </div>
            
            <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-[#00FF66] transition-colors" />
        </Link>
    );
}

export function GamificationLeaderboardPage() {
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('all');
    const [stats, setStats] = useState(null);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                // Fetch leaderboard
                const response = await fetch(`${API_URL}/api/leaderboard/gamification?limit=50`);
                if (response.ok) {
                    const data = await response.json();
                    setLeaderboard(data);
                }
                
                // Fetch platform stats
                const statsRes = await fetch(`${API_URL}/api/stats`);
                if (statsRes.ok) {
                    const statsData = await statsRes.json();
                    setStats(statsData);
                }
            } catch (error) {
                console.error('Failed to fetch leaderboard:', error);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [period]);

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="gamification-leaderboard-page">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="flex items-center justify-center gap-3 mb-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FFD700]/20 to-[#FF6B6B]/20 flex items-center justify-center">
                        <Trophy className="w-7 h-7 text-[#FFD700]" />
                    </div>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                    <span className="text-[#FFD700]">XP</span> Leaderboard
                </h1>
                <p className="text-white/50 max-w-md mx-auto">
                    Top players ranked by experience points. Earn XP by betting, winning, and collecting badges!
                </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <Card className="bg-white/[0.02] border-white/5">
                    <CardContent className="p-4 text-center">
                        <Users className="w-5 h-5 text-[#00FF66] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-white">{leaderboard.length}</div>
                        <div className="text-xs text-white/40">Active Players</div>
                    </CardContent>
                </Card>
                <Card className="bg-white/[0.02] border-white/5">
                    <CardContent className="p-4 text-center">
                        <Zap className="w-5 h-5 text-[#FFD700] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-white">
                            {leaderboard[0]?.xp?.toLocaleString() || 0}
                        </div>
                        <div className="text-xs text-white/40">Top XP</div>
                    </CardContent>
                </Card>
                <Card className="bg-white/[0.02] border-white/5">
                    <CardContent className="p-4 text-center">
                        <Trophy className="w-5 h-5 text-[#9B6FE8] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-white">
                            {leaderboard.reduce((sum, p) => sum + (p.badge_count || 0), 0)}
                        </div>
                        <div className="text-xs text-white/40">Total Badges</div>
                    </CardContent>
                </Card>
                <Card className="bg-white/[0.02] border-white/5">
                    <CardContent className="p-4 text-center">
                        <Star className="w-5 h-5 text-[#FF6B6B] mx-auto mb-2" />
                        <div className="text-2xl font-bold text-white">
                            {Math.max(...leaderboard.map(p => p.level?.level || 1), 1)}
                        </div>
                        <div className="text-xs text-white/40">Highest Level</div>
                    </CardContent>
                </Card>
            </div>

            {/* Period Filter */}
            <Tabs value={period} onValueChange={setPeriod} className="mb-6">
                <TabsList className="bg-white/5 border border-white/10">
                    <TabsTrigger 
                        value="all"
                        className="data-[state=active]:bg-[#FFD700]/10 data-[state=active]:text-[#FFD700]"
                    >
                        All Time
                    </TabsTrigger>
                    <TabsTrigger 
                        value="month"
                        className="data-[state=active]:bg-[#FFD700]/10 data-[state=active]:text-[#FFD700]"
                        disabled
                    >
                        This Month
                    </TabsTrigger>
                    <TabsTrigger 
                        value="week"
                        className="data-[state=active]:bg-[#FFD700]/10 data-[state=active]:text-[#FFD700]"
                        disabled
                    >
                        This Week
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {loading ? (
                <div className="space-y-4">
                    <Skeleton className="h-32 w-full bg-white/5" />
                    {Array(5).fill(0).map((_, i) => (
                        <Skeleton key={i} className="h-20 w-full bg-white/5" />
                    ))}
                </div>
            ) : leaderboard.length === 0 ? (
                <Card className="bg-white/[0.02] border-white/5">
                    <CardContent className="p-12 text-center">
                        <Sparkles className="w-12 h-12 text-white/20 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-white mb-2">No Players Yet</h3>
                        <p className="text-white/50 mb-4">Be the first to earn XP and climb the leaderboard!</p>
                        <Link to="/create">
                            <Button className="bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52]">
                                Create Your First Bet
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* Podium for Top 3 */}
                    {leaderboard.length >= 3 && <Podium players={leaderboard.slice(0, 3)} />}
                    
                    {/* Leaderboard List */}
                    <div className="space-y-3">
                        {leaderboard.slice(3).map((player, idx) => (
                            <LeaderboardRow key={player.address} player={player} rank={idx + 4} />
                        ))}
                    </div>
                    
                    {/* All players if less than 4 */}
                    {leaderboard.length < 4 && leaderboard.length > 0 && (
                        <div className="space-y-3 mt-8">
                            {leaderboard.map((player, idx) => (
                                <LeaderboardRow key={player.address} player={player} rank={idx + 1} />
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* XP Guide */}
            <Card className="bg-white/[0.02] border-white/5 mt-8">
                <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-[#FFD700]" />
                        How to Earn XP
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5 text-center">
                            <div className="text-[#00FF66] font-bold text-xl">+10</div>
                            <div className="text-xs text-white/50">Create Bet</div>
                        </div>
                        <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5 text-center">
                            <div className="text-[#00FF66] font-bold text-xl">+25</div>
                            <div className="text-xs text-white/50">Win Bet</div>
                        </div>
                        <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5 text-center">
                            <div className="text-[#FFD700] font-bold text-xl">+50</div>
                            <div className="text-xs text-white/50">Use Oracle</div>
                        </div>
                        <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5 text-center">
                            <div className="text-[#9B6FE8] font-bold text-xl">+100</div>
                            <div className="text-xs text-white/50">Earn Badge</div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
